// ported from existing ATCMS implementation
// see:  https://github.com/apartmenttherapy/atcms/blob/master/app/assets/javascripts/site/third_party/comscore/asset_labels.js
import { getFormattedDate } from 'oxygen-lib-ui/helpers/date';
import { SITE_NAME } from 'constants/site';
import { COMSCORE_SLUG } from 'constants/vendor';
import { PUBLISHER_ID } from './index';

const durationInMs = secs => secs * 1000;

const intersection = (a, b) => a.filter(x => b.includes(x));

export const CONTENT_CLASSIFICATION = 'vc11';
export const AD_CLASSIFICATION = 'va11';
export const NULL_VAL = '*null';

// this format string uses the timezone lib format
const DATE_FORMAT = '%Y-%m-%d';

// See story for attached StreamingAnalytics implementation details
// https://app.asana.com/0/233552004682384/263179370733081
export const GENRES = [
  'House Tours',
  'How-To',
  'DIY',
  'Food How-To',
  'Recipes',
  'Food Techniques'
];
export const SERIES = ['House Tours', 'DIY', 'Food How-To', 'Food Techniques'];

// according to comScore docs these are mandatory
export const DEFAULT_ASSET_LABELS = {
  ns_st_ci: NULL_VAL, // content id
  ns_st_cl: NULL_VAL, // content duration
  ns_st_pu: NULL_VAL, // * publisher brand name
  ns_st_pr: NULL_VAL, // * program title, such as a series name
  ns_st_ep: NULL_VAL, // * content title
  ns_st_sn: NULL_VAL, // * episode season number in 2+ digits (01, 02, 10, etc)
  ns_st_en: NULL_VAL, // * episode number in 2+ digits (01, 02, 10, etc)
  ns_st_ge: NULL_VAL, // * genre
  ns_st_ti: NULL_VAL, // * TMS / Gracenote ID (standardized id for video assets)
  ns_st_ia: NULL_VAL, // * are online ad loads the same as TV ad loads?
  ns_st_ddt: NULL_VAL, // * pubdate yyyy-mm-dd,
  ns_st_tdt: NULL_VAL, // * TV air date
  ns_st_st: NULL_VAL, // * station title, such as BBC
  // labels specific to non-ad content
  ns_st_pn: '1', // segment number
  ns_st_tp: '1', // total number of mid-roll ad pods + 1
  ns_st_ce: '1', // * complete episode flag; full media or excerpt?
  // labels specific to ad content
  ns_st_ad: '0' // ad flag: pre-roll, mid-roll, post-roll, 1 (ad with undetermined type), or 0 (not an ad)
};

/**
 * assembles Comscore labels based on provided video player asset
 *
 * @param {Object} asset playlist item (video)
 * @param {String} asset.mediaid video ID
 * @param {Number} asset.duration video duration in seconds
 * @param {String} asset.title video title
 * @param {Number} asset.pubdate video publication date in unix epoch seconds
 * @param {String} asset.tags comma-delimited list of tags
 * @returns {Object} comscore object
 */
export const getContentLabels = asset => {
  const { mediaid, duration, title, pubdate, tags } = asset;

  const allTags = tags.split(',');
  const seriesTags = intersection(allTags, SERIES).join(', ');
  const genreTags = intersection(allTags, GENRES).join(', ');
  const formattedDate = getFormattedDate(pubdate, DATE_FORMAT);

  return {
    ns_st_ci: mediaid,
    ns_st_cl: `${durationInMs(duration)}`,
    ns_st_pu: SITE_NAME,
    ns_st_pr: seriesTags || NULL_VAL,
    ns_st_ep: title,
    ns_st_ge: genreTags || NULL_VAL,
    ns_st_ddt: formattedDate,
    ns_st_ct: CONTENT_CLASSIFICATION
  };
};

/**
 * assembles Comscore labels based on provided ad asset from video player
 *
 * @param {Object} asset playlist item (ad)
 * @param {String} asset.id ad ID
 * @param {Number} asset.duration ad duration in seconds
 * @param {String} asset.adtitle ad title
 * @param {String} asset.adposition ad position
 * @returns {Object} comscore object
 */
export const getAdLabels = asset => {
  const { id, adtitle, adposition } = asset;

  let duration = 0;
  // guard against undefined keys
  try {
    duration = asset.ima.ad.g.duration;
  } catch (e) {}

  return {
    ns_st_ci: id,
    ns_st_cl: `${durationInMs(duration)}`,
    ns_st_ep: adtitle,
    ns_st_ad: adposition || '1',
    ns_st_ct: AD_CLASSIFICATION
  };
};

/**
 * assembles Video Metrix object based on provided category (née vertical)
 *
 * @param {String} category category name (née vertical)
 * @returns {Object} video metrix object
 */
export const getVideoMetrixLabels = category => ({
  c2: PUBLISHER_ID,
  c3: COMSCORE_SLUG,
  c4: category || NULL_VAL,
  c6: NULL_VAL
});
